import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { Loading } from '../../../../../../components/Loading/Loading';
import MainContainerWithWindow from '../../../../../../components/MainContainerWithWindow/MainContainerWithWindow';
import { pushNotify } from '../../../../../../components/Notifications';
import { useLazyDownloadSettlementsQuery } from '../../../../../../redux/services/SettlementsApi';
import { Selector, TItem } from './SelectType';
import styles from './DownloadSettlements.module.css';

interface Inputs {
  direction: 'all' | 'M2P' | 'P2M';
  status: 'all' | 'created' | 'in_progress' | 'rejected' | 'completed';
  date_from: string;
  date_to: string;
}

export const DownloadSettlements: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const methods = useForm<Inputs>({
    defaultValues: {
      direction: 'all',
      status: 'all',
      date_from: '',
      date_to: '',
    },
  });
  const { handleSubmit, watch, setValue, register } = methods;

  const [trigger, { data: blob, isFetching, isError }] =
    useLazyDownloadSettlementsQuery();
  const [downloaded, setDownloaded] = useState(false);

  const directions: TItem<'all' | 'M2P' | 'P2M'>[] = [
    { id: 'all',  name: 'Все' },
    { id: 'M2P',  name: 'Мерчант → Площадка' },
    { id: 'P2M',  name: 'Площадка → Мерчант' },
  ];
  const statuses: TItem<'all' | 'created' | 'in_progress' | 'rejected' | 'completed'>[] = [
    { id: 'all',         name: 'Все' },
    { id: 'created',     name: 'Создано' },
    { id: 'in_progress', name: 'В процессе' },
    { id: 'rejected',    name: 'Отклонено' },
    { id: 'completed',   name: 'Завершено' },
  ];

  // после загрузки — скачиваем и закрываем
  useEffect(() => {
    if (!blob || downloaded) return;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const ts = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
    link.download = `Settlements_${ts}.xlsx`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setDownloaded(true);
    onClose();
  }, [blob, downloaded, onClose]);

  if (!open) return null;
  const portal = (node: React.ReactNode) =>
    createPortal(node, document.body);

  // Один и тот же обёртка с backdrop для спиннера и формы
  const withOverlay = (content: React.ReactNode) => portal(
    <>
      <div className={styles.backdrop} onClick={onClose} />
      {content}
    </>
  );

      if (isFetching) {
        return createPortal(
          <MainContainerWithWindow>
            <Loading />
          </MainContainerWithWindow>,
          document.body
        );
      }


  if (isError) {
    pushNotify('Не удалось скачать выписки', 'Error');
    onClose();
    return null;
  }

  return withOverlay(
    <FormProvider {...methods}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Скачать выписки</h2>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.subtitle}>Выберите необходимые фильтры</div>
        <div className={styles.loadingContainer}>
          {isFetching && <Loading />}
        </div>
        <form
          onSubmit={handleSubmit(data => {
            setDownloaded(false);
            trigger({
              direction: data.direction,
              status: data.status,
              date_from: data.date_from || undefined,
              date_to: data.date_to || undefined,
            });
          })}
          className={styles.form}
        >
          <Selector
            title="Направление"
            items={directions}
            value={watch('direction')}
            onChange={v => setValue('direction', v)}
          />

          <Selector
            title="Статус"
            items={statuses}
            value={watch('status')}
            onChange={v => setValue('status', v)}
          />

          <div className={styles.dateRow}>
            <div className={styles.field}>
              <label className={styles.label}>Дата с</label>
              <input
                type="date"
                {...register('date_from')}
                className={styles.input}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Дата по</label>
              <input
                type="date"
                {...register('date_to')}
                className={styles.input}
              />
            </div>
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={!watch('date_from') || !watch('date_to')}
          >
            Скачать
          </button>
        </form>
      </div>
    </FormProvider>
  );
};
